import * as THREE from 'three';
import type { ShowBlock } from '@vrlearning/lesson-schema';
import type { StatefulBlock } from './features.js';
import { setOpacity, type PartRegistry } from './parts.js';

/** How far in a part is grown: scaled up from its lowest point (the market's stalls, townsquare.ts). */
export function growFromFoot(object: THREE.Object3D, foot: number, amount: number): void {
  const y = Math.max(0.01, amount);
  object.scale.y = y;
  // Scaling about the object's origin moves its foot; put the foot back where it stood.
  object.position.y = foot - (foot - object.userData['restY']) * y;
}

/** The show block (V2): its parts shown at once (at half way), grown from their foot, or faded. */
export function buildShowBlock(
  root: THREE.Object3D,
  block: ShowBlock,
  parts: PartRegistry,
): StatefulBlock {
  const bound: { mesh: THREE.Mesh; foot: number }[] = [];
  let amount = block.visible ? 1 : 0;
  const apply = (): void => {
    for (const part of bound) {
      if (block.way === 'meteen') part.mesh.visible = amount >= 0.5;
      else part.mesh.visible = amount > 0.01;
      if (block.way === 'groeien') growFromFoot(part.mesh, part.foot, amount);
      // The part's shared copies (the colour block's too): the fade multiplies its opacity.
      if (block.way === 'vervagen')
        for (const material of parts.materialsOf(part.mesh)) setOpacity(material, { fade: amount });
    }
  };
  parts.when(block.parts, (meshes) => {
    for (const mesh of meshes) {
      mesh.userData['restY'] = mesh.position.y;
      // The foot in the part's parent space, where its position is.
      mesh.updateMatrix();
      if (!mesh.geometry.boundingBox) mesh.geometry.computeBoundingBox();
      const foot = mesh.geometry.boundingBox!.clone().applyMatrix4(mesh.matrix).min.y;
      if (block.way === 'vervagen') parts.materialsOf(mesh);
      bound.push({ mesh, foot });
    }
    apply();
  });
  apply();
  return {
    set(setting, value) {
      if (setting !== 'visible') return;
      amount = value as number;
      apply();
    },
  };
}
